import { useEffect, useState } from "react";
import { BellRing, Loader2 } from "lucide-react";
import { useGlobalToast } from "../contexts/ToastContext";
import {
  getWebPushStatus,
  enableWebPush,
  disableWebPush,
  type WebPushStatus,
} from "@/lib/web-push";

/**
 * Fila "Avisos en este dispositivo" del desplegable de la campana
 * (15-sep-2026): activa Web Push en el PWA/navegador actual para recibir los
 * avisos de conversaciones (y el numerito del icono) con la app cerrada. No
 * se pinta donde no hay soporte (Safari en pestaña, iPhone sin instalar).
 */
export default function WebPushToggle() {
  const [status, setStatus] = useState<WebPushStatus | "loading" | null>(null);
  const { showToast } = useGlobalToast();

  useEffect(() => {
    let active = true;
    getWebPushStatus().then((s) => {
      if (active) setStatus(s);
    });
    return () => {
      active = false;
    };
  }, []);

  if (status === null || status === "unsupported") return null;

  const enable = async () => {
    setStatus("loading");
    const result = await enableWebPush();
    if (result.ok) {
      setStatus("active");
      showToast("Notificaciones activadas en este dispositivo", "success");
    } else {
      setStatus(await getWebPushStatus());
      showToast(result.reason ?? "No se pudieron activar las notificaciones", "error");
    }
  };

  const disable = async () => {
    setStatus("loading");
    await disableWebPush();
    setStatus("inactive");
    showToast("Notificaciones desactivadas en este dispositivo", "info");
  };

  return (
    <div className="border-t border-gray-100 px-4 py-2.5 flex items-center justify-between gap-2">
      <span className="flex items-center gap-1.5 text-xs text-gray-500 min-w-0">
        <BellRing className="w-3.5 h-3.5 flex-shrink-0" />
        Avisos en este dispositivo
      </span>
      {status === "denied" ? (
        <span className="text-xs text-gray-400">Bloqueados por el navegador</span>
      ) : status === "loading" ? (
        <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
      ) : status === "active" ? (
        <button
          onClick={disable}
          className="text-xs font-medium text-gray-400 hover:text-gray-600"
        >
          Desactivar
        </button>
      ) : (
        <button
          onClick={enable}
          className="text-xs font-semibold text-primary-600 hover:text-primary-700"
        >
          Activar
        </button>
      )}
    </div>
  );
}
